import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "./services/utils";
import { useKeycloak } from "./KeycloakProvider";
import getEnv from "./config";

// Default: all modules disabled (pessimistic). Overridden by /modules/status response.
const DEFAULT_MODULES = {
  crawlers: false,
  social_crawlers: false,
  cti_gathering: false,
  extractor: false,
  ontologies: false,
  ids: false,
  taxonomies: false,
  correlations: false,
};

const ModuleContext = createContext({
  modules: DEFAULT_MODULES,
  available: [],
  loaded: false,
  settled: false,
  toggleModule: () => {},
});

export const useModules = () => useContext(ModuleContext);

const AUTH_ENABLED = getEnv("REACT_APP_AUTH_ENABLED") === "true";

export const ModuleProvider = ({ children }) => {
  const [modules, setModules] = useState(DEFAULT_MODULES);
  const [available, setAvailable] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [settled, setSettled] = useState(!AUTH_ENABLED);
  const { initialized } = useKeycloak();

  const applyModuleStatus = useCallback((res) => {
    const { _available, ...status } = res.data;
    setModules({ ...DEFAULT_MODULES, ...status });
    if (_available) setAvailable(_available);
  }, []);

  // Track whether Effect B has delivered auth-enriched data
  const hasAuthData = React.useRef(false);

  // Effect A: Immediate fetch — unblocks the app with global defaults (token attached if already available)
  useEffect(() => {
    api
      .get("/modules/status")
      .then((res) => {
        if (!hasAuthData.current) applyModuleStatus(res);
      })
      .catch(() => {
        // If unreachable, modules stay all-false (pessimistic)
      })
      .finally(() => {
        setLoaded(true);
      });
  }, [applyModuleStatus]);

  // Effect B: Auth-enriched fetch — refines to org-specific module overrides
  useEffect(() => {
    if (!AUTH_ENABLED || !initialized) return;
    api
      .get("/modules/status")
      .then((res) => {
        hasAuthData.current = true;
        applyModuleStatus(res);
      })
      .catch(() => {
        // Global defaults from Effect A remain valid
      })
      .finally(() => {
        setSettled(true);
      });
  }, [initialized, applyModuleStatus]);

  const toggleModule = useCallback((key, enabled) => {
    setModules((prev) => {
      const previous = prev[key];
      // Optimistic update
      api
        .patch("/modules/status", { [key]: enabled })
        .catch(() => setModules((cur) => ({ ...cur, [key]: previous })));
      return { ...prev, [key]: enabled };
    });
  }, []);

  return (
    <ModuleContext.Provider value={{ modules, available, loaded, settled, toggleModule }}>
      {children}
    </ModuleContext.Provider>
  );
};
